import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { loadSellerTicketAverageReport } from '../services'
import { SellerTicketAverageFilters, SellerTicketAverageReport } from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const sourceName = (source: 'ninegrid' | 'totvs'): string =>
  source === 'ninegrid' ? 'Ninegrid' : 'TOTVS'

const sourceColorClass = (source: 'ninegrid' | 'totvs'): string =>
  source === 'ninegrid' ? 'bg-primary' : 'bg-warning'

const exportToCSV = (filename: string, report: SellerTicketAverageReport | null): void => {
  let csv = 'sep=;\n'
  csv += 'Origem;Pedidos;Faturamento;Ticket medio\n'

  ;(report?.sourceBreakdown || []).forEach((item) => {
    csv += `"${sourceName(item.source)}";${item.orderCount};${item.totalAmountCents / 100};${item.averageTicketCents / 100}\n`
  })

  const summary = report?.summary
  if (summary) {
    csv += '\nRESUMO\n'
    csv += 'Pedidos totais;Faturamento total\n'
    csv += `${summary.totalOrders};${summary.totalAmountCents / 100}\n`
  }

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, report: SellerTicketAverageReport | null): void => {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    (report?.sourceBreakdown || []).map((item) => ({
      Origem: sourceName(item.source),
      Pedidos: item.orderCount,
      Faturamento: item.totalAmountCents / 100,
      'Ticket medio': item.averageTicketCents / 100,
    }))
  )

  XLSX.utils.book_append_sheet(workbook, sheet, 'Origem')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, report: SellerTicketAverageReport | null): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Comparativo por Origem', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Pedidos totais: ${report?.summary.totalOrders || 0} | Faturamento total: ${formatCurrency(report?.summary.totalAmountCents || 0)}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Origem', 'Pedidos', 'Faturamento', 'Ticket medio']],
    body: (report?.sourceBreakdown || []).map((item) => [
      sourceName(item.source),
      item.orderCount,
      formatCurrency(item.totalAmountCents),
      formatCurrency(item.averageTicketCents),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SourceComparisonReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const summary = report?.summary
  const sourceBreakdown = useMemo(() => report?.sourceBreakdown || [], [report?.sourceBreakdown])

  const largestSource = useMemo(() => {
    if (!sourceBreakdown.length) {
      return null
    }

    return [...sourceBreakdown].sort((left, right) => right.totalAmountCents - left.totalAmountCents)[0]
  }, [sourceBreakdown])

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Comparativo por Origem', path: '/seller/reports/source-comparison', isActive: true }]}>
        Relatório de Origens (Ninegrid x TOTVS)
      </PageTitle>

      <ReportShell
        title='Comparativo por Origem de Pedidos'
        description='Comparação entre Ninegrid e TOTVS para pedidos, faturamento e ticket médio.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Comparativo-Origem-${timestamp}.csv`, report)
            }} disabled={loading || !report?.sourceBreakdown.length} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Comparativo-Origem-${timestamp}.xlsx`, report)
            }} disabled={loading || !report?.sourceBreakdown.length} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Comparativo-Origem-${timestamp}.pdf`, report)
            }} disabled={loading || !report?.sourceBreakdown.length} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Pedidos totais</div>
                <div className='fs-2hx fw-bold text-primary'>
                  {(summary?.totalOrders || 0).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Faturamento total</div>
                <div className='fs-2hx fw-bold text-success'>
                  {formatCurrency(summary?.totalAmountCents || 0)}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Origem com maior receita</div>
                <div className='fs-3 fw-bold text-dark'>
                  {largestSource ? sourceName(largestSource.source) : 'Sem dados'}
                </div>
                <div className='text-gray-700 fw-semibold mt-2'>
                  {largestSource ? formatCurrency(largestSource.totalAmountCents) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm mb-4'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Receita por origem</span>
              <span className='text-gray-600 mt-1'>Distribuição percentual do faturamento total.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {sourceBreakdown.map((item) => {
              const revenueShare =
                (summary?.totalAmountCents || 0) > 0
                  ? (item.totalAmountCents / (summary?.totalAmountCents || 1)) * 100
                  : 0

              return (
                <div key={item.source} className='mb-6'>
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <div className='fw-semibold text-gray-700'>{sourceName(item.source)}</div>
                    <div className='fw-bold text-dark'>{formatCurrency(item.totalAmountCents)}</div>
                  </div>
                  <div className='progress h-10px'>
                    <div
                      className={`progress-bar ${sourceColorClass(item.source)}`}
                      role='progressbar'
                      style={{ width: `${Math.min(100, Math.max(0, revenueShare))}%` }}
                      aria-valuenow={Math.round(revenueShare)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className='text-end text-gray-600 fs-7 mt-1'>
                    {percentFormatter.format(revenueShare)}%
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Detalhamento por origem</span>
              <span className='text-gray-600 mt-1'>Pedidos, faturamento e ticket médio por fonte.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : sourceBreakdown.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Origem</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ticket médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceBreakdown.map((item) => (
                      <tr key={item.source}>
                        <td>
                          <div className='fw-bold text-dark'>{sourceName(item.source)}</div>
                        </td>
                        <td className='text-end'>{item.orderCount}</td>
                        <td className='text-end fw-bold'>{formatCurrency(item.totalAmountCents)}</td>
                        <td className='text-end'>{formatCurrency(item.averageTicketCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SourceComparisonReportPage }